import { useCustomerCompanyList } from "@boundless/bl-api-client/customer/services"
import { useNavigate } from "@tanstack/react-router"
import { Building2, ChevronsUpDown } from "lucide-react"
import { toast } from "sonner"
import { useStore } from "zustand"

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { companyListQueryOptions } from "@/lib/companies"
import { store } from "@/lib/store"
import { cn } from "@/lib/utils"

import type { Company } from "@boundless/bl-api-client/customer/models"

interface CompanySwitcher {
  companies: Company[]
  active: Company
  setActive: (uuid: string) => void
}

/**
 * Active company selection shared by the desktop sidebar and the mobile "More"
 * menu, so both surfaces gate and switch identically.
 *
 * Returns `null` when there is nothing to switch: non-customers, or customers
 * with fewer than two companies.
 */
export function useCompanySwitcher(): CompanySwitcher | null {
  const userRole = useStore(store, (state) => state.userRole)
  const activeCompanyUuid = useStore(store, (state) => state.activeCompanyUuid)
  const navigate = useNavigate()

  // Same cache key `CompanyWrapper` populates, which already gates the whole
  // dashboard on it. The shared options are mandatory, not a convenience - see
  // `companyListQueryOptions`.
  const { data: companies } = useCustomerCompanyList(
    {},
    companyListQueryOptions(userRole === "customer"),
  )

  if (userRole !== "customer" || !companies || companies.length < 2) {
    return null
  }

  // Copy before sorting: `companies` is TanStack Query's cached array, shared
  // with `CompanyWrapper`. Alphabetical keeps rows positionally stable across
  // visits, unlike the recency ordering the list is fetched with.
  const sorted = [...companies].sort((a, b) =>
    a.legalName.localeCompare(b.legalName),
  )
  const active = sorted.find((c) => c.uuid === activeCompanyUuid) ?? sorted[0]

  const setActive = (uuid: string) => {
    const next = sorted.find((c) => c.uuid === uuid)
    if (!next || next.uuid === active.uuid) {
      return
    }

    // The destination has to be derived from the store *after* it moves, and
    // from a route that tears down the current company-scoped matches, so the
    // switch always lands via `/resume`.
    store.setState({ activeCompanyUuid: uuid })
    toast.success(`Switched to ${next.legalName}`)
    navigate({ to: "/resume" })
  }

  return { companies: sorted, active, setActive }
}

/**
 * Menu rows for the switcher, rendered inside a caller's
 * `DropdownMenuContent`.
 */
export function CompanySwitcherMenuItems({
  companies,
  active,
  setActive,
}: CompanySwitcher) {
  return (
    <>
      <DropdownMenuLabel>Switch company</DropdownMenuLabel>
      {/* Bound the rows rather than the content: `DropdownMenuContent` caps at
          viewport height, which would push a caller's own items out of reach. */}
      <div className="max-h-56 overflow-y-auto">
        <DropdownMenuRadioGroup value={active.uuid} onValueChange={setActive}>
          {companies.map((company) => (
            <DropdownMenuRadioItem key={company.uuid} value={company.uuid}>
              <span className="truncate">{company.legalName}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </div>
    </>
  )
}

/**
 * Desktop switcher, hosted at the top of the sidebar so it scopes the nav items
 * below it. The sidebar rather than the header because `embed_hide_header` can
 * remove the header entirely, which would leave a desktop embed with no
 * switcher at all - the mobile bottom nav does not render above `lg`.
 *
 * On mobile the same rows live in the bottom nav's "More" menu, and the sidebar
 * itself is hidden, so exactly one trigger exists at any breakpoint.
 */
export function CompanySwitcher({ isCollapsed }: { isCollapsed: boolean }) {
  const switcher = useCompanySwitcher()

  if (!switcher) {
    return null
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          // Collapsed leaves only the icon on a 64px rail, so the label has to
          // survive as a tooltip - same pattern as the collapsed nav links.
          title={isCollapsed ? switcher.active.legalName : undefined}
          className={cn(
            "hover:bg-muted/60 mb-4 flex w-full items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm font-medium shadow-sm transition-colors",
            isCollapsed && "justify-center px-2",
          )}
        >
          <Building2 className="text-muted-foreground size-4 shrink-0" />
          {!isCollapsed && (
            <>
              <span className="truncate">{switcher.active.legalName}</span>
              <ChevronsUpDown className="text-muted-foreground ml-auto size-3.5 shrink-0" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <CompanySwitcherMenuItems {...switcher} />
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
